import React from "react";
import { Button, Typography, Box } from "@mui/material";
import type { SurveyQuestion } from "posthog-js";

/**
 * Props for the RatingQuestion component.
 */
export interface RatingQuestionProps {
  /** The SurveyQuestion object with a scale property */
  question: SurveyQuestion;
  /** The current numeric value or null */
  value: number | null;
  /** Callback when the value changes */
  onChange: (value: number) => void;
}

/**
 * RatingQuestion Component
 * Renders a survey question with a numeric rating scale using numbered buttons.
 */
const RatingQuestion: React.FC<RatingQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  // Only proceed if the question defines a scale property
  if (!("scale" in question)) {
    // Render nothing if scale is missing
    return null;
  }

  // Extract scale value or default to 5
  const scale = question.scale ?? 5;
  // Optional text labels for endpoints
  const lowerLabel =
    "lowerBoundLabel" in question ? question.lowerBoundLabel : "";
  const upperLabel =
    "upperBoundLabel" in question ? question.upperBoundLabel : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Render numbered buttons for each scale point (including zero) */}
      <Box sx={{ display: "flex", gap: 0.25, width: "100%" }}>
        {Array.from({ length: scale + 1 }, (_, i) => i).map((num) => (
          <Button
            key={num}
            variant={value === num ? "contained" : "outlined"}
            size="small"
            onClick={() => onChange(num)}
            disableRipple
            sx={{
              flex: 1,
              minWidth: 0,
              height: 32,
              p: 0,
              fontSize: "0.875rem",
            }}
          >
            {num}
          </Button>
        ))}
      </Box>
      {/* Labels describing the endpoints of the scale */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          0 - {lowerLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {scale} - {upperLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default RatingQuestion;
